import React, { useMemo, useState } from "react";
import { t } from "ttag";
import Picker from "react-mobile-picker";
import { ChevronDown } from "@coolremote/shared";
import {
  IModbusDeviceRow,
  UI_TYPE_BINARY,
  UI_TYPE_ENUMERATION,
  UI_TYPE_REVERSE_BINARY,
  UI_TYPE_VALUE,
} from "../types";
import { engUnitLabel } from "../RegisterTable";
import styles from "./ModbusUnitControl.module.css";

// ─── Register key helpers ─────────────────────────────────────────────────────

const REGISTER_TYPE_NAMES: Record<number, string> = { 0: "coil", 1: "input", 2: "hreg", 3: "ireg" };

export const getModbusRegisterKey = (row: IModbusDeviceRow): string | null => {
  if (row.registerAddress == null) return null;
  const name = REGISTER_TYPE_NAMES[row.registerType];
  if (!name) {
    return null;
  }
  const suffix = row.bitNumber != null ? `#${row.bitNumber}` : "";
  return `${name}:${row.registerAddress}${suffix}`;
};

// Schedule API object format: "<registerType>:<registerAddress>",
// where registerType is numeric (0=coil, 1=input, 2=hreg, 3=ireg).
// This differs from getModbusRegisterKey's name-based key ("hreg:25").
export const getModbusObjectKey = (row: IModbusDeviceRow): string | null => {
  if (row.registerAddress == null || row.registerType == null) {
    return null;
  }
  const suffix = row.bitNumber != null ? `#${row.bitNumber}` : "";
  return `${row.registerType}:${row.registerAddress}${suffix}`;
};

// Reverse of getModbusObjectKey: converts the schedule's "<typeCode>:<address>"
// back to the app's "hreg:<address>" register key for lookup.
export const objectKeyToRegisterKey = (objectKey: string | null | undefined): string | null => {
  if (!objectKey) {
    return null;
  }
  const separatorIndex = objectKey.indexOf(":");
  if (separatorIndex === -1) {
    return null;
  }
  const typeCode = objectKey.slice(0, separatorIndex);
  const rest = objectKey.slice(separatorIndex + 1);
  const name = REGISTER_TYPE_NAMES[Number(typeCode)];
  if (!name || !rest) {
    return null;
  }
  return `${name}:${rest}`;
};

// ─── Wheel picker row (list + numeric) ───────────────────────────────────────

interface IWheelRowProps {
  label: string;
  displayValue: string;
  options: string[];            // display strings shown in the wheel
  selectedOption: string;       // currently selected display string
  readOnly?: boolean;
  onSelect: (option: string) => void;
}

const WheelRow: React.FC<IWheelRowProps> = ({
  label,
  displayValue,
  options,
  selectedOption,
  readOnly = false,
  onSelect,
}) => {
    const [open, setOpen] = useState(false);

  const handleToggle = () => {
    if (!readOnly) setOpen((v) => !v);
  };

  return (
    <li className={readOnly ? styles.rowReadOnly : styles.pickerContainer}>
      <div className={styles.pickerHeader} onClick={handleToggle}>
        <span className={styles.rowLabel}>{label}</span>
        <div className={styles.rowRight}>
          <span className={styles.valueText}>{displayValue}</span>
          {!readOnly && (
            <ChevronDown
              className={styles.chevron}
              style={open ? { transform: "rotate(180deg)" } : undefined}
            />
          )}
        </div>
      </div>
      {open && !readOnly && (
        <div className={styles.pickerWheelWrapper}>
          <Picker
            optionGroups={{ value: options }}
            valueGroups={{ value: selectedOption }}
            onChange={(_name: string, val: string) => onSelect(val)}
            wheel="normal"
            height={150}
            itemHeight={30}
          />
        </div>
      )}
    </li>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export interface IModbusUnitControlProps {
  unitName: string;
  rows: IModbusDeviceRow[];
  operationalData?: Record<string, number | string | null | undefined>;
  readOnly?: boolean;
  onSetValue?: (row: IModbusDeviceRow, rowIndex: number, value: number) => void;
}

const ModbusUnitControl: React.FC<IModbusUnitControlProps> = ({
  unitName,
  rows,
  operationalData = {},
  readOnly = false,
  onSetValue,
}) => {
    const [activePickerId, setActivePickerId] = useState<string | null>(null);

  const getRawValue = (row: IModbusDeviceRow): number | null => {
    const key = getModbusRegisterKey(row);
    const raw = key != null ? operationalData[key] : undefined;
    if (raw === undefined || raw === null) return null;
    const n = Number(raw);
    return isNaN(n) ? null : n;
  };

  const getDisplayText = (row: IModbusDeviceRow): string => {
    const value = getRawValue(row);
    if (value === null) return "—";
    switch (row.uiType) {
      case UI_TYPE_BINARY: {
        const key = String(Math.round(value));
        return row.customEnum?.[key] ?? (value ? t`True` : t`False`);
      }
      case UI_TYPE_REVERSE_BINARY: {
        const displayVal = value === 0 ? 1 : 0;
        const key = String(displayVal);
        return row.customEnum?.[key] ?? (displayVal ? t`True` : t`False`);
      }
      case UI_TYPE_ENUMERATION: {
        const key = String(Math.round(value));
        return row.customEnum?.[key] ?? key;
      }
      case UI_TYPE_VALUE:
      default:
        return `${value}${row.units ? ` ${engUnitLabel(row.units)}` : ""}`;
    }
  };

  const renderRow = (row: IModbusDeviceRow, rowIndex: number) => {
    const value = getRawValue(row);
    const isRowReadOnly = readOnly || row.readOnly;
    const rowKey = `${rowIndex}`;

    // ── Read-only display (all types) ────────────────────────────────────────
    if (isRowReadOnly) {
      return (
        <li key={rowIndex} className={styles.rowReadOnly}>
          <div className={styles.pickerHeader}>
            <span className={styles.rowLabel}>{row.name}</span>
            <div className={styles.rowRight}>
              <span className={styles.valueText}>{getDisplayText(row)}</span>
            </div>
          </div>
        </li>
      );
    }

    // ── Binary ────────────────────────────────────────────────────────────────
    if (row.uiType === UI_TYPE_BINARY) {
      const trueLabel = row.customEnum?.["1"] ?? t`True`;
      const falseLabel = row.customEnum?.["0"] ?? t`False`;
      const options = [trueLabel, falseLabel];
      const selected = value != null ? (Math.round(value) === 1 ? trueLabel : falseLabel) : falseLabel;
      return (
        <WheelRow
          key={rowIndex}
          label={row.name}
          displayValue={getDisplayText(row)}
          options={options}
          selectedOption={selected}
          onSelect={(opt) => onSetValue?.(row, rowIndex, opt === trueLabel ? 1 : 0)}
        />
      );
    }

    // ── Reverse binary ────────────────────────────────────────────────────────
    if (row.uiType === UI_TYPE_REVERSE_BINARY) {
      const trueLabel = row.customEnum?.["1"] ?? t`True`;
      const falseLabel = row.customEnum?.["0"] ?? t`False`;
      const options = [trueLabel, falseLabel];
      // Wire: True=0, False=1 (reverse)
      const selected = value != null ? (Math.round(value) === 0 ? trueLabel : falseLabel) : trueLabel;
      return (
        <WheelRow
          key={rowIndex}
          label={row.name}
          displayValue={getDisplayText(row)}
          options={options}
          selectedOption={selected}
          onSelect={(opt) => onSetValue?.(row, rowIndex, opt === trueLabel ? 0 : 1)}
        />
      );
    }

    // ── Enumeration ───────────────────────────────────────────────────────────
    if (row.uiType === UI_TYPE_ENUMERATION) {
      const entries = Object.entries(row.customEnum || {});
      if (!entries.length) {
        return (
          <li key={rowIndex} className={styles.rowReadOnly}>
            <div className={styles.pickerHeader}>
              <span className={styles.rowLabel}>{row.name}</span>
              <div className={styles.rowRight}>
                <span className={styles.valueText}>{getDisplayText(row)}</span>
              </div>
            </div>
          </li>
        );
      }
      const options = entries.map(([, label]) => label as string);
      const selectedKey = value != null ? String(Math.round(value)) : entries[0][0];
      const selected = row.customEnum?.[selectedKey] ?? options[0];
      return (
        <WheelRow
          key={rowIndex}
          label={row.name}
          displayValue={getDisplayText(row)}
          options={options}
          selectedOption={selected}
          onSelect={(opt) => {
            const found = entries.find(([, v]) => v === opt);
            if (found) onSetValue?.(row, rowIndex, Number(found[0]));
          }}
        />
      );
    }

    // ── Numeric value ─────────────────────────────────────────────────────────
    if (row.uiType === UI_TYPE_VALUE) {
      return (
        <NumericWheelRow
          key={rowIndex}
          row={row}
          value={value}
          displayValue={getDisplayText(row)}
          activePickerId={activePickerId}
          onPickerToggle={(id) => setActivePickerId((prev) => (prev === id ? null : id))}
          rowKey={rowKey}
          onSetValue={(v) => onSetValue?.(row, rowIndex, v)}
        />
      );
    }

    // ── Default (fallback read-only) ──────────────────────────────────────────
    return (
      <li key={rowIndex} className={styles.rowReadOnly}>
        <div className={styles.pickerHeader}>
          <span className={styles.rowLabel}>{row.name}</span>
          <div className={styles.rowRight}>
            <span className={styles.valueText}>{getDisplayText(row)}</span>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className={styles.card}>
      {rows.length === 0 ? (
        <div className={styles.empty}>{t`No registers configured.`}</div>
      ) : (
        <ul className={styles.rowList}>
          {rows.map((row, rowIndex) => renderRow(row, rowIndex))}
        </ul>
      )}
    </div>
  );
};

// ─── Numeric wheel row (matches BACnet NumericControl) ────────────────────────

interface INumericWheelRowProps {
  row: IModbusDeviceRow;
  value: number | null;
  displayValue: string;
  activePickerId: string | null;
  onPickerToggle: (id: string) => void;
  rowKey: string;
  onSetValue: (v: number) => void;
}

const MAX_WHEEL_ITEMS = 500;

const NumericWheelRow: React.FC<INumericWheelRowProps> = ({
  row, value, displayValue, activePickerId, onPickerToggle, rowKey, onSetValue,
}) => {
    const isOpen = activePickerId === rowKey;

  const { options, closestOption } = useMemo(() => {
    const min = row.minValue ?? 10;
    const max = row.maxValue ?? 50;
    const step = row.step ?? 1;
    const count = Math.round((max - min) / step) + 1;

    if (count > MAX_WHEEL_ITEMS) return { options: null, closestOption: "" };

    const opts: string[] = [];
    for (let v = min; v <= max; v += step) {
      opts.push(`${parseFloat(v.toFixed(10))}${row.units ? ` ${engUnitLabel(row.units)}` : ""}`);
    }

    const numVal = value ?? min;
    const closest =
      opts.find((o) => parseFloat(o) === numVal) ||
      opts.reduce((prev, curr) =>
        Math.abs(parseFloat(curr) - numVal) < Math.abs(parseFloat(prev) - numVal) ? curr : prev
      );

    return { options: opts, closestOption: closest };
  }, [row.minValue, row.maxValue, row.step, row.units, value]);

  // Fallback: stepper when range is too large or unbounded
  if (!options) {
    return (
      <StepperRow
        row={row}
        value={value}
        displayValue={displayValue}
        onSetValue={onSetValue}
      />
    );
  }

  return (
    <li className={styles.pickerContainer}>
      <div className={styles.pickerHeader} onClick={() => onPickerToggle(rowKey)}>
        <span className={styles.rowLabel}>{row.name}</span>
        <div className={styles.rowRight}>
          <span className={styles.valueText}>{displayValue}</span>
          <ChevronDown
            className={styles.chevron}
            style={isOpen ? { transform: "rotate(180deg)" } : undefined}
          />
        </div>
      </div>
      {isOpen && (
        <div className={styles.pickerWheelWrapper}>
          <Picker
            optionGroups={{ value: options }}
            valueGroups={{ value: closestOption }}
            onChange={(_name: string, opt: string) => {
              const num = parseFloat(opt.split(" ")[0]);
              if (!isNaN(num)) onSetValue(num);
            }}
            wheel="normal"
            height={150}
            itemHeight={30}
          />
        </div>
      )}
    </li>
  );
};

// ─── Stepper fallback for unbounded numeric ranges ────────────────────────────

interface IStepperRowProps {
  row: IModbusDeviceRow;
  value: number | null;
  displayValue: string;
  onSetValue: (v: number) => void;
}

const StepperRow: React.FC<IStepperRowProps> = ({ row, value, displayValue, onSetValue }) => {
    const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState("");

  const current = value ?? 0;
  const min = row.minValue;
  const max = row.maxValue;
  const step = row.step ?? 1;

  const clamp = (v: number) => {
    if (min != null && v < min) return min;
    if (max != null && v > max) return max;
    return v;
  };
  const round = (v: number) => parseFloat(v.toFixed(10));

  const canDown = min == null || current - step >= min;
  const canUp = max == null || current + step <= max;

  const commitInput = () => {
    const n = parseFloat(inputVal);
    if (!isNaN(n)) onSetValue(round(clamp(n)));
    setEditing(false);
  };

  return (
    <li className={styles.pickerContainer}>
      <div className={styles.pickerHeader}>
        <span className={styles.rowLabel}>{row.name}</span>
        <div className={styles.rowRight}>
          <div className={styles.numericStepper}>
            <button className={styles.stepBtn} onClick={() => onSetValue(round(clamp(current - step)))} disabled={!canDown}>−</button>
            {editing ? (
              <input
                autoFocus
                type="number"
                className={styles.stepInput}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onBlur={commitInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitInput();
                  if (e.key === "Escape") setEditing(false);
                }}
              />
            ) : (
              <span
                className={styles.stepValue}
                title={t`Click to type a value`}
                onClick={() => { setInputVal(value != null ? String(value) : ""); setEditing(true); }}
              >
                {displayValue}
              </span>
            )}
            <button className={styles.stepBtn} onClick={() => onSetValue(round(clamp(current + step)))} disabled={!canUp}>+</button>
          </div>
        </div>
      </div>
    </li>
  );
};

export default ModbusUnitControl;
export { ModbusUnitControl };
